import { useState, useEffect } from 'react';
import { Button, Input, Modal, Select, Space, Table, Tag, Typography } from 'antd';
import { FolderOpenOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { metadataApi } from '@api/metadataApi';
import type { DynamicDocumentDto } from '@/types/metadata';

interface DocumentPickerProps {
  /** Збережене значення (documentId або dynamicDocumentId — single UUID). */
  acqValue?: string | null;
  acqOnChange?: (acqId: string | null, acqLabel?: string) => void;
  acqReadOnly?: boolean;
  acqAllowClear?: boolean;
  acqDisabled?: boolean;
  acqSize?: 'small' | 'middle' | 'large';
  /**
   * Опційний фільтр — лише документи цього типу. Якщо порожньо — користувач сам обирає тип у модалці.
   * (BAS doc-refs зазвичай polymorphic, тип невідомий — тому фільтр має бути runtime).
   */
  acqDocTypeCode?: string;
}

/**
 * Picker для polymorphic document reference (referenceEntity = "Document").
 * Дозволяє вибрати будь-який документ із списку Dynamic Documents.
 * UI: Tag (read-only) або кнопка "Обрати документ…" → модалка з фільтром по типу + табл.
 */
export default function DocumentPicker({
  acqValue, acqOnChange, acqReadOnly = false, acqAllowClear = true,
  acqDisabled, acqSize = 'middle', acqDocTypeCode,
}: DocumentPickerProps) {
  const [acqOpen, setAcqOpen] = useState(false);

  // Резолв збереженого id → документ
  const acqCurrentQ = useQuery({
    queryKey: ['doc', 'one', acqValue],
    queryFn: () => acqValue ? metadataApi.getDynamicDocument(acqValue) : Promise.resolve(null),
    enabled: !!acqValue,
    staleTime: 5 * 60_000,
  });

  // ── Read-only вид
  if (acqReadOnly) {
    if (!acqValue) return <Typography.Text type="secondary">—</Typography.Text>;
    if (acqCurrentQ.isLoading) return <Typography.Text type="secondary">…</Typography.Text>;
    if (acqCurrentQ.data) {
      return (
        <Tag style={{ fontSize: 13, padding: '2px 8px' }} title={`id: ${acqValue}`}>
          {acqDocLabel(acqCurrentQ.data)}
        </Tag>
      );
    }
    return <Tag color="default" title={`id: ${acqValue}`}>[doc] {String(acqValue).slice(0, 8)}…</Tag>;
  }

  const acqLabel = acqCurrentQ.data ? acqDocLabel(acqCurrentQ.data) : (acqValue ? `id: ${String(acqValue).slice(0, 8)}…` : '');

  return (
    <>
      <Space.Compact style={{ width: '100%' }}>
        <Input
          value={acqLabel}
          readOnly
          placeholder="Документ не обрано"
          size={acqSize}
          allowClear={acqAllowClear && !!acqValue}
          onChange={(e) => {
            // allowClear тригерить onChange з пустим value
            if (!e.target.value && acqOnChange) acqOnChange(null);
          }}
          disabled={acqDisabled}
        />
        <Button
          size={acqSize}
          icon={<FolderOpenOutlined />}
          disabled={acqDisabled}
          onClick={() => setAcqOpen(true)}
          title="Обрати документ"
        />
      </Space.Compact>
      <DocumentListModal
        acqOpen={acqOpen}
        acqInitialDocType={acqDocTypeCode}
        acqOnCancel={() => setAcqOpen(false)}
        acqOnSelect={(d) => {
          setAcqOpen(false);
          if (acqOnChange) acqOnChange(d.id, acqDocLabel(d));
        }}
      />
    </>
  );
}

function acqDocLabel(d: DynamicDocumentDto): string {
  const acqDate = dayjs(d.docDate).format('DD.MM.YYYY');
  return `${d.docTypeCode} № ${d.docNumber ?? '—'} від ${acqDate}`;
}

function DocumentListModal({
  acqOpen, acqInitialDocType, acqOnCancel, acqOnSelect,
}: {
  acqOpen: boolean;
  acqInitialDocType?: string;
  acqOnCancel: () => void;
  acqOnSelect: (acqDoc: DynamicDocumentDto) => void;
}) {
  const [acqDocType, setAcqDocType] = useState<string | undefined>(acqInitialDocType);

  const acqTypesQ = useQuery({
    queryKey: ['metadata', 'document-types'],
    queryFn: () => metadataApi.getDocumentTypes(true),
    enabled: acqOpen,
    staleTime: 5 * 60_000,
  });

  const acqDocsQ = useQuery({
    queryKey: ['metadata', 'dynamic-documents', acqDocType],
    queryFn: () => metadataApi.getDynamicDocuments(acqDocType, 1, 100),
    enabled: acqOpen && !!acqDocType,
  });

  useEffect(() => {
    if (!acqOpen) setAcqDocType(acqInitialDocType);
  }, [acqOpen, acqInitialDocType]);

  const acqDocs = acqDocsQ.data ?? [];
  const acqTypes = acqTypesQ.data ?? [];

  return (
    <Modal
      open={acqOpen}
      title="Обрати документ"
      onCancel={acqOnCancel}
      footer={null}
      width={820}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Select
          showSearch
          placeholder="Спочатку оберіть тип документа…"
          value={acqDocType}
          onChange={setAcqDocType}
          options={acqTypes.map((t) => ({ value: t.code, label: `${t.name} (${t.code})` }))}
          filterOption={(input, opt) => String(opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          loading={acqTypesQ.isLoading}
          style={{ width: '100%' }}
        />
        <Table<DynamicDocumentDto>
          rowKey="id"
          size="small"
          dataSource={acqDocs}
          loading={acqDocsQ.isLoading || acqDocsQ.isFetching}
          pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
          columns={[
            { title: '№', dataIndex: 'docNumber', width: 120, render: (v: string | null) => v ?? <Typography.Text type="secondary">—</Typography.Text> },
            { title: 'Дата', dataIndex: 'docDate', width: 120, render: (v: string) => dayjs(v).format('DD.MM.YYYY') },
            { title: 'Сума', dataIndex: 'totalAmount', width: 120, render: (v: number) => v.toFixed(2) },
            { title: 'Стан', dataIndex: 'isPosted', width: 100, render: (v: boolean) => v ? <Tag color="green">Проведено</Tag> : <Tag>Чернетка</Tag> },
          ]}
          onRow={(row) => ({ onClick: () => acqOnSelect(row), style: { cursor: 'pointer' } })}
          locale={{ emptyText: acqDocType ? 'Немає документів цього типу' : 'Оберіть тип документа вище' }}
        />
      </Space>
    </Modal>
  );
}
